







import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
    Save, ArrowLeft, Plus, Trash2, Search, ChevronDown, ChevronUp, 
    User, MapPin, Briefcase, CreditCard, Users, ShieldCheck, HardDrive 
} from "lucide-react";

const MemberForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const memberId = searchParams.get("id");

    const [loading, setLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [errors, setErrors] = useState({}); // Used for validation highlighting
    const [activeSection, setActiveSection] = useState("personal");

    // Suggestions UI State
    const [plotSuggestions, setPlotSuggestions] = useState([]);
    const [activePlotIndex, setActivePlotIndex] = useState(null);
    const [agentSuggestions, setAgentSuggestions] = useState([]); // Changed from staff
const [activeAgentField, setActiveAgentField] = useState(null); // Changed from staff
const [branches, setBranches] = useState([]);
    const dropdownRef = useRef(null);

    const initialFormState = {
        memberId: null, plotId: null, staffId: null, branchId: null,
        memberName: "", profileImageName: "", aadharNo: "",
        name2: "", doB2: "", dateofBirth: "", marriageDate: "",
        address: "", city: "", postCode: "", phoneNo: "", email: "", 
        workTelephone: "", homeTelephone: "",
        nomineeName: "", relationship: "",
        membershipNo: "", memberType: "", membershipFee: 0, paidAmount: 0, balanceAmount: 0, 
        salary: "0", amc: "0", emi: "", emiPerAmount: "", noOfYears: "",
        branch: "", salesExecutive: "", takeOverManager: "", branchIncharge: "", 
        teleExecutive: "", venueManager: "", branchManager: "", bdExecutive: "",
        salesConsultantCode: "", salesManagerCode: "", telemarketingCode: "", 
        branchHeadCode: "", bdExecutiveCode: "",
        status: "ACTIVE", twoWheeler: "", fourWheeler: "", reason: "", 
        joiningDate: "", dealDate: "", plotNumbers: "",
        plotList: [""],
        plotIds: [] 
    };

    const [formData, setFormData] = useState(initialFormState);

    const toggleSection = (section) => {
        setActiveSection(activeSection === section ? null : section);
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr.startsWith("0000")) return "";
        return dateStr.split('T')[0];
    };

    // --- VALIDATION LOGIC ---
    const validate = () => {
        let newErrors = {};
        if (!formData.memberName?.trim()) newErrors.memberName = true;
        if (!formData.address?.trim()) newErrors.address = true;
        if (!formData.phoneNo?.trim()) newErrors.phoneNo = true;
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- FETCH DATA FOR EDIT ---
    useEffect(() => {
        if (memberId) {
            const fetchMember = async () => {
                setIsFetching(true);
                try {
                    const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}/${memberId}`);
                    const data = await res.json();
                    
                    const fetchedPlotList = data.memberPlots && data.memberPlots.length > 0 
                        ? data.memberPlots.map(mp => `${mp.plot.plotNumber} / ${mp.plot.totalArea} / ${mp.plot.project?.projectName || 'N/A'}`)
                        : (data.plotNumbers ? data.plotNumbers.split(',').map(s => s.trim()) : [""]);

                    const fetchedPlotIds = data.memberPlots && data.memberPlots.length > 0
                        ? data.memberPlots.map(mp => mp.plotId)
                        : (data.plotId ? [data.plotId] : []);

                    setFormData({
                        ...initialFormState,
                        ...data,
                        dateofBirth: formatDate(data.dateofBirth),
                        marriageDate: formatDate(data.marriageDate),
                        joiningDate: formatDate(data.joiningDate),
                        dealDate: formatDate(data.dealDate),
                        doB2: formatDate(data.doB2),
                        plotList: fetchedPlotList,
                        plotIds: fetchedPlotIds
                    });
                } catch (e) {
                    Swal.fire("Error", "Could not fetch member details", "error");
                } finally { setIsFetching(false); }
            };
            fetchMember();
        } else {
            setFormData(initialFormState);
        }
    }, [memberId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Remove error highlight when user starts typing
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: false }));
    };

    useEffect(() => {
    const loadBranches = async () => {
        try {
            const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_GET_ALL}?pageSize=1000`);
            const data = await res.json();
            setBranches(data.data || []);
        } catch (e) { console.error("Branch load error", e); }
    };
    loadBranches();
}, []); // The [] ensures this only runs once, making the dropdown instant.
    
    const selectPlot = (index, plot) => {
        if (plot.plotStatus === "Sold") return;
        const newList = [...formData.plotList];
        const newIds = [...(formData.plotIds || [])];
        newList[index] = `${plot.plotNumber} / ${plot.totalArea} / ${plot.project?.projectName || 'N/A'}`;
        newIds[index] = plot.plotId; 
        setFormData(prev => ({ ...prev, plotList: newList, plotIds: newIds, plotId: newIds[0] }));
        setActivePlotIndex(null);
    };

    const handlePlotSearch = async (index, term) => {
        const newList = [...formData.plotList];
        newList[index] = term;
        setFormData(prev => ({ ...prev, plotList: newList }));
        setActivePlotIndex(index);
        try {
            const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PLOT_GET_ALL}?searchTerm=${term}`);
            const result = await res.json();
            setPlotSuggestions(result.data || []);
        } catch (e) { setPlotSuggestions([]); }
    };

    const handleAgentSearch = async (term, field) => {
    setFormData(prev => ({ ...prev, [field]: term }));
    setActiveAgentField(field);
        try {
           const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.AGENT_GET_ALL}?searchTerm=${term}`);
        const result = await res.json();
        setAgentSuggestions(result.data || []);
    } catch (e) { setAgentSuggestions([]); }
    };

    const selectAgent = (agent, field) => {
        setFormData(prev => ({ 
            ...prev, 
             [field]: agent.name,
            [`${field}Code`]: agent.code || agent.phoneNo,
            agentId: field === "salesExecutive" ? agent.agentId : prev.agentId // Use agentId
        }));
       setActiveAgentField(null);
    };

    const handleSubmit = async (e) => {
        if(e) e.preventDefault();
        
        if (!validate()) {
            Swal.fire("Required Fields", "Please fill in all mandatory fields highlighted in red.", "warning");
            return;
        }

        setLoading(true);
        const payload = {
            ...formData,
            plotIds: (formData.plotIds || []).filter(id => id),
            plotNumbers: formData.plotList.filter(p => p.trim()).join(', '),
            salary: String(formData.salary),
            amc: String(formData.amc)
        };

        try {
            const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const result = await res.json();
            if (result.success) {
                Swal.fire("Success", "Record saved perfectly", "success");
                navigate("/members");
            } else { Swal.fire("Error", result.message, "error"); }
        } catch (e) { Swal.fire("Error", "Server Connection Failed", "error"); }
        setLoading(false);
    };

    const styles = {
        wrapper: { backgroundColor: "#f4f7fe", minHeight: "100vh", padding: "20px 5%", fontFamily: "'Inter', sans-serif" },
        header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" },
       accordion: (active) => ({ 
    backgroundColor: "#fff", 
    borderRadius: "15px", 
    marginBottom: "15px", 
    boxShadow: "0 4px 15px rgba(0,0,0,0.03)", 
    position: "relative", 
    zIndex: active ? 10 : 1 
}),
        accordionHeader: (active) => ({ 
            padding: "20px 25px", display: "flex", justifyContent: "space-between", alignItems: "center", 
            cursor: "pointer", background: active ? "#4f46e5" : "#fff", color: active ? "#fff" : "#1b2559",
            transition: "all 0.3s ease"
        }),
        content: { padding: "25px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "25px" },
        inputGroup: { display: "flex", flexDirection: "column", gap: "8px", position: "relative" },
        label: { fontSize: "12px", fontWeight: "800", color: "#a3aed0", textTransform: "uppercase" },
        input: (hasError) => ({ 
            padding: "12px 16px", borderRadius: "12px", 
            border: hasError ? "1.5px solid #fa0509" : "1.5px solid #e2e8f0", 
            outline: "none", fontSize: "14px", color: "#1b2559", width: '100%', boxSizing: 'border-box',
            backgroundColor: hasError ? "#fff5f5" : "#fff"
        }),
        saveBtn: { backgroundColor: "#4f46e5", color: "#fff", padding: "14px 45px", borderRadius: "12px", border: "none", fontWeight: "700", cursor: "pointer" },
        suggestionBox: { position: "absolute", zIndex: 5000, background: "#fff", width: "100%", boxShadow: "0 10px 30px rgba(0,0,0,0.15)", borderRadius: "12px", top: "75px", maxHeight: "200px", overflowY: "auto" }
    };

    if (isFetching) return <div style={{padding:'100px', textAlign:'center'}}>Loading member profile...</div>;

    return (
        <>
            <Navbar />
            <div style={styles.wrapper}>
                <div style={styles.header}>
                    <button onClick={() => navigate("/members")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", color: "#fa0509", fontWeight: "700" }}>
                        <ArrowLeft size={18} /> Back
                    </button>
                    <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1b2559" }}>{memberId ? "Edit Member Profile" : "Register New Member"}</h2>
                    <button onClick={handleSubmit} style={styles.saveBtn}>{loading ? "Saving..." : "Save Record"}</button>
                </div>

                <form>
                    {/* SECTION 1: PERSONAL */}
                    <div style={styles.accordion(activeSection === "personal")}>
                        <div style={styles.accordionHeader(activeSection === "personal")} onClick={() => toggleSection("personal")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <User size={20} /> <span style={{ fontWeight: "800" }}>Primary Personal Details</span>
                            </div>
                            {activeSection === "personal" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "personal" && (
                            <div style={styles.content}>
                                <div style={styles.inputGroup}><label style={styles.label}>Full Name *</label><input name="memberName" value={formData.memberName} onChange={handleInputChange} style={styles.input(errors.memberName)} /></div>
                               
                                <div style={styles.inputGroup}><label style={styles.label}>Aadhar Number</label><input name="aadharNo" value={formData.aadharNo} onChange={handleInputChange} style={styles.input()} /></div>
                                
                               
                                <div style={styles.inputGroup}><label style={styles.label}>Date of Birth</label><input type="date" name="dateofBirth" value={formData.dateofBirth} onChange={handleInputChange} style={styles.input()} /></div>
                            </div>
                        )}
                    </div>

                    {/* SECTION 2: CO-APPLICANT */}
                    <div style={styles.accordion(activeSection === "spouse")}>
                        <div style={styles.accordionHeader(activeSection === "spouse")} onClick={() => toggleSection("spouse")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Users size={20} /> <span style={{ fontWeight: "800" }}>Spouse & Anniversary</span>
                            </div>
                            {activeSection === "spouse" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "spouse" && (
                            <div style={styles.content}>
                                <div style={styles.inputGroup}><label style={styles.label}>Spouse Name</label><input name="name2" value={formData.name2} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Spouse DOB</label><input type="date" name="doB2" value={formData.doB2} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Marriage Anniversary</label><input type="date" name="marriageDate" value={formData.marriageDate} onChange={handleInputChange} style={styles.input()} /></div>
                            </div>
                        )}
                    </div>

                    {/* SECTION 3: CONTACT */}
                    <div style={styles.accordion(activeSection === "contact")}>
                        <div style={styles.accordionHeader(activeSection === "contact")} onClick={() => toggleSection("contact")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <MapPin size={20} /> <span style={{ fontWeight: "800" }}>Contact & Address</span>
                            </div>
                            {activeSection === "contact" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "contact" && (
                            <div style={styles.content}>
                                <div style={{ gridColumn: "span 2", ...styles.inputGroup }}><label style={styles.label}>Residential Address *</label><input name="address" value={formData.address} onChange={handleInputChange} style={styles.input(errors.address)} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>City</label><input name="city" value={formData.city} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Post Code</label><input name="postCode" value={formData.postCode} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Primary Mobile *</label><input name="phoneNo" value={formData.phoneNo} onChange={handleInputChange} style={styles.input(errors.phoneNo)} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Email ID</label><input name="email" value={formData.email} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Work Tel</label><input name="workTelephone" value={formData.workTelephone} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Home Tel</label><input name="homeTelephone" value={formData.homeTelephone} onChange={handleInputChange} style={styles.input()} /></div>
                            </div>
                        )}
                    </div>

                    {/* SECTION 4: NOMINEE */}
                    <div style={styles.accordion(activeSection === "nominee")}>
                        <div style={styles.accordionHeader(activeSection === "nominee")} onClick={() => toggleSection("nominee")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <ShieldCheck size={20} /> <span style={{ fontWeight: "800" }}>Nominee Details</span>
                            </div>
                            {activeSection === "nominee" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "nominee" && (
                            <div style={styles.content}>
                                <div style={styles.inputGroup}><label style={styles.label}>Nominee Name</label><input name="nomineeName" value={formData.nomineeName} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Relationship</label><input name="relationship" value={formData.relationship} onChange={handleInputChange} style={styles.input()} /></div>
                            </div>
                        )}
                    </div>

                    {/* SECTION 5: FINANCIALS */}
                    <div style={styles.accordion(activeSection === "finance")}>
                        <div style={styles.accordionHeader(activeSection === "finance")} onClick={() => toggleSection("finance")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <CreditCard size={20} /> <span style={{ fontWeight: "800" }}>Financials & Membership</span>
                            </div>
                            {activeSection === "finance" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "finance" && (
                            <div style={styles.content}>
                                <div style={styles.inputGroup}><label style={styles.label}>Membership No</label><input name="membershipNo" value={formData.membershipNo} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Member Type</label><input name="memberType" value={formData.memberType} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Membership Fee</label><input type="number" name="membershipFee" value={formData.membershipFee} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Paid Amount</label><input type="number" name="paidAmount" value={formData.paidAmount} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Total Balance (Plot)</label><input type="number" name="balanceAmount" style={{ ...styles.input(), color: "#fa0509", fontWeight: "bold" }} value={formData.balanceAmount} onChange={handleInputChange} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Monthly Salary</label><input name="salary" value={formData.salary} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>AMC</label><input name="amc" value={formData.amc} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>EMI Status</label><input name="emi" value={formData.emi} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>EMI Per Month</label><input name="emiPerAmount" value={formData.emiPerAmount} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>No Of Years</label><input name="noOfYears" value={formData.noOfYears} onChange={handleInputChange} style={styles.input()} /></div>
                            </div>
                        )}
                    </div>

                    {/* SECTION 6: PLOTS */}
                    <div style={styles.accordion(activeSection === "plots")}>
                        <div style={styles.accordionHeader(activeSection === "plots")} onClick={() => toggleSection("plots")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <HardDrive size={20} /> <span style={{ fontWeight: "800" }}>Plot Assignment</span>
                            </div>
                            {activeSection === "plots" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "plots" && (
                            <div style={{ ...styles.content, gridTemplateColumns: "1fr" }}>
                                {formData.plotList.map((p, idx) => (
                                    <div key={idx} style={{ display: "flex", gap: "15px", position: "relative" }}>
                                        <div style={{ flex: 1, position: "relative" }}>
                                            <input style={styles.input()} value={p} onChange={(e) => handlePlotSearch(idx, e.target.value)} placeholder="Search Plot No..." />
                                            {activePlotIndex === idx && plotSuggestions.length > 0 && (
                                                <div style={styles.suggestionBox}>
                                                    {plotSuggestions.map(ps => (
                                                        <div key={ps.plotId} onClick={() => selectPlot(idx, ps)} style={{ padding: "12px", cursor: ps.plotStatus === "Sold" ? "not-allowed" : "pointer", borderBottom: "1px solid #f1f5f9" }}>
                                                            {ps.plotNumber} / {ps.totalArea} / {ps.project?.projectName || 'N/A'} - {ps.plotStatus}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {formData.plotList.length > 1 && (
                                            <button type="button" onClick={() => {
                                                const newList = formData.plotList.filter((_, i) => i !== idx);
                                                const newIds = formData.plotIds.filter((_, i) => i !== idx);
                                                setFormData(prev => ({ ...prev, plotList: newList, plotIds: newIds }));
                                            }} style={{ background: "#fee2e2", border: "none", borderRadius: "10px", padding: "10px", color: "#fa0509", cursor: "pointer" }}><Trash2 size={20} /></button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, plotList: [...prev.plotList, ""], plotIds: [...prev.plotIds, null] }))} style={{ width: "fit-content", background: "#eef2ff", color: "#4f46e5", border: "none", padding: "10px 25px", borderRadius: "10px", fontWeight: "bold", cursor: "pointer" }}>+ Link Another Plot</button>
                            </div>
                        )}
                    </div>

                    {/* SECTION 7: STAFF */}
                    <div style={styles.accordion(activeSection === "staff")}>
                        <div style={styles.accordionHeader(activeSection === "staff")} onClick={() => toggleSection("staff")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Briefcase size={20} /> <span style={{ fontWeight: "800" }}>Executive Assignments</span>
                            </div>
                            {activeSection === "staff" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "staff" && (
                            <div style={styles.content}>
                              <div style={styles.inputGroup}>
    <label style={styles.label}>Branch *</label>
    <select 
        name="branchId" 
        style={styles.input(errors.branchId)} 
        value={formData.branchId || ""} 
        onChange={(e) => {
            const selectedBranch = branches.find(b => b.branchId === e.target.value);
            setFormData(prev => ({ 
                ...prev, 
                branchId: e.target.value, 
                branch: selectedBranch ? selectedBranch.branchName : "" 
            }));
        }}
    >
        <option value="">Select Branch...</option>
        {branches.map(b => (
            <option key={b.branchId} value={b.branchId}>
                {b.branchName}
            </option>
        ))}
    </select>
</div>
                                {["salesExecutive", "teleExecutive", "branchManager", "bdExecutive", "takeOverManager", "branchIncharge", "venueManager"].map(field => (
    <div key={field} style={styles.inputGroup}>
        <label style={styles.label}>{field.replace(/([A-Z])/g, ' $1')}</label>
        <input style={styles.input()} value={formData[field]} onChange={(e) => handleAgentSearch(e.target.value, field)} placeholder="Search Agent..." />
        {activeAgentField === field && agentSuggestions.length > 0 && (
            <div style={styles.suggestionBox}>
                {agentSuggestions.map(a => (
                    <div key={a.agentId} style={{ padding: "12px", cursor: "pointer" }} onClick={() => selectAgent(a, field)}>{a.name} ({a.code})</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* SECTION 8: SYSTEM */}
                    <div style={styles.accordion(activeSection === "system")}>
                        <div style={styles.accordionHeader(activeSection === "system")} onClick={() => toggleSection("system")}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <ShieldCheck size={20} /> <span style={{ fontWeight: "800" }}>System Status & Tracking</span>
                            </div>
                            {activeSection === "system" ? <ChevronUp /> : <ChevronDown />}
                        </div>
                        {activeSection === "system" && (
                            <div style={styles.content}>
                                <div style={styles.inputGroup}><label style={styles.label}>Status</label><select name="status" style={styles.input()} value={formData.status} onChange={handleInputChange}><option value="ACTIVE">ACTIVE</option><option value="INACTIVE">INACTIVE</option></select></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Two Wheeler</label><input name="twoWheeler" value={formData.twoWheeler} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Four Wheeler</label><input name="fourWheeler" value={formData.fourWheeler} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Joining Date</label><input type="date" name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={styles.inputGroup}><label style={styles.label}>Deal Date</label><input type="date" name="dealDate" value={formData.dealDate} onChange={handleInputChange} style={styles.input()} /></div>
                                <div style={{ gridColumn: "span 2", ...styles.inputGroup }}><label style={styles.label}>Reason/Notes</label><input name="reason" value={formData.reason} onChange={handleInputChange} style={styles.input()} /></div>
                            </div>
                        )}
                    </div>
                </form>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "20px", marginTop: "50px", marginBottom: "100px" }}>
                    <button onClick={() => navigate("/members")} style={{ padding: "15px 40px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: "700", color: "#a3aed0", cursor: "pointer" }}>Discard Changes</button>
                    <button onClick={handleSubmit} style={styles.saveBtn}>Finalize Registration</button>
                </div>
            </div>
        </>
    );
};

export default MemberForm;